import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignIn, useClerk } from '@clerk/clerk-react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { OTPInput } from '../components/auth/OTPInput';
import { ClerkStatusNotice } from '../components/auth/ClerkStatusNotice';
import {
  authErrorMessage, clerkErrorCode, exchangeClerkSession, linkExistingAccount, routeAfterLogin,
} from '../lib/clerkAuth';

const RESET = 'reset_password_email_code';

export default function ForgotPassword() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const clerk = useClerk();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [loading, setLoading] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;
    setLoading(true);
    const attempt = () => signIn.create({ strategy: RESET, identifier: email.trim() });
    try {
      try {
        await attempt();
      } catch (err) {
        if (clerkErrorCode(err) !== 'form_identifier_not_found') throw err;
        await linkExistingAccount(email.trim());
        await attempt();
      }
      setStep('code');
      toast.success('If that email is registered, a 6-digit code is on its way.');
    } catch (error) {
      if (clerkErrorCode(error) === 'form_identifier_not_found') {
        setStep('code');
        toast.success('If that email is registered, a 6-digit code is on its way.');
      } else {
        toast.error(authErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result = await signIn.attemptFirstFactor({ strategy: RESET, code, password });
      if (result.status === 'needs_new_password') {
        result = await signIn.resetPassword({ password, signOutOfOtherSessions: true });
      }
      if (result.status !== 'complete') throw new Error('Reset needs another step. Please try again.');
      await setActive({ session: result.createdSessionId });
      const data = await exchangeClerkSession(clerk, { password_reset: true });
      toast.success('Password updated. You have been signed out on every other device.');
      await routeAfterLogin(data, { navigate, checkAuth });
    } catch (error) {
      toast.error(authErrorMessage(error, 'Reset failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-4xl font-medium text-[#1A1A1A]">Zuriya</h1>
          </Link>
          <p className="text-stone-500 mt-2">Reset your password</p>
        </div>
        <div className="card-surface p-8">
          <ClerkStatusNotice />
          {step === 'email' ? (
            <form onSubmit={sendCode} className="space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="input-zuriya pl-10" required data-testid="forgot-email-input" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary h-12" disabled={loading || !isLoaded} data-testid="forgot-submit">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</> : 'Send me a code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={resetPassword} className="space-y-5" data-testid="reset-code-step">
              <p className="text-sm text-stone-500 text-center">
                Enter the 6-digit code sent to <span className="font-medium text-[#1A1A1A]">{email}</span> and choose a new password.
              </p>
              <OTPInput value={code} onChange={setCode} disabled={loading} />
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="New password" className="input-zuriya pl-10" required minLength={8} data-testid="reset-password-input" />
                </div>
              </div>
              <Button type="submit" className="w-full btn-primary h-12" disabled={loading || code.length !== 6 || password.length < 8} data-testid="reset-password-submit">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[#D4AF37] hover:underline font-medium inline-flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
